import React, { useCallback, useRef, useEffect } from "react";

// ─────────────────────────────────────────────
//  Types
// ─────────────────────────────────────────────
export type MoveDir = { forward: boolean; backward: boolean; left: boolean; right: boolean };
export type LookDelta = { dx: number; dy: number };

interface GameControlsProps {
  onMoveChange: (move: MoveDir) => void;
  onLookDelta: (delta: LookDelta) => void;
  onJump: () => void;
}

// ─────────────────────────────────────────────
//  Responsive sizing based on viewport
// ─────────────────────────────────────────────
// D-Pad and Joystick: 6% desktop, 8% mobile
const isDesktop = window.innerWidth > 768;
const BASE_SIZE = window.innerWidth * (isDesktop ? 0.03 : 0.11);

// Tính toán kích thước dựa trên BASE_SIZE
const BTN  = BASE_SIZE;           // D-pad button size
const GAP  = BASE_SIZE * 1.08;    // offset from center to button (maintain aspect ratio)
const JOY  = BASE_SIZE * 2.1;     // look joystick outer diameter
const KNOB = BASE_SIZE * 0.85;    // look joystick inner knob

// Bottom & horizontal margins
const BOT  = window.innerHeight * 0.10;
const HOR  = BASE_SIZE * 0.6;

// ─────────────────────────────────────────────
//  D-Pad button component
// ─────────────────────────────────────────────
function DPadButton({
  label,
  style,
  onStart,
  onEnd,
}: {
  label: React.ReactNode;
  style?: React.CSSProperties;
  onStart: () => void;
  onEnd: () => void;
}) {
  const handlePointerDown = (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    e.stopPropagation();
    onStart();
  };
  const handlePointerUp = (e: React.PointerEvent) => {
    e.stopPropagation();
    onEnd();
  };

  return (
    <button
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      onPointerCancel={handlePointerUp}
      style={{
        position: "absolute",
        width: BTN,
        height: BTN,
        borderRadius: BTN * 0.2,
        background: "rgba(255,255,255,0.15)",
        border: `${Math.max(1, BTN * 0.08)}px solid rgba(255,255,255,0.45)`,
        color: "#fff",
        fontSize: BTN * 0.42,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        userSelect: "none",
        WebkitUserSelect: "none",
        touchAction: "none",
        backdropFilter: "blur(4px)",
        boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
        transition: "background 0.1s",
        ...style,
      }}
    >
      {label}
    </button>
  );
}

// ─────────────────────────────────────────────
//  Look joystick (right side)
// ─────────────────────────────────────────────
function LookJoystick({ onLookDelta }: { onLookDelta: (d: LookDelta) => void }) {
  const trackingRef = useRef<{ pointerId: number; lastX: number; lastY: number } | null>(null);
  const rafRef = useRef<number | null>(null);
  const deltaAccRef = useRef<LookDelta>({ dx: 0, dy: 0 });

  const flush = useCallback(() => {
    if (deltaAccRef.current.dx !== 0 || deltaAccRef.current.dy !== 0) {
      onLookDelta({ ...deltaAccRef.current });
      deltaAccRef.current = { dx: 0, dy: 0 };
    }
    rafRef.current = requestAnimationFrame(flush);
  }, [onLookDelta]);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(flush);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [flush]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    e.stopPropagation();
    trackingRef.current = { pointerId: e.pointerId, lastX: e.clientX, lastY: e.clientY };
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!trackingRef.current || trackingRef.current.pointerId !== e.pointerId) return;
    e.stopPropagation();
    const dx = e.clientX - trackingRef.current.lastX;
    const dy = e.clientY - trackingRef.current.lastY;
    trackingRef.current.lastX = e.clientX;
    trackingRef.current.lastY = e.clientY;
    deltaAccRef.current.dx += dx;
    deltaAccRef.current.dy += dy;
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    trackingRef.current = null;
  };

  return (
    <div
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      onPointerCancel={handlePointerUp}
      style={{
        position: "fixed",
        bottom: BOT,
        right: HOR,
        width: JOY,
        height: JOY,
        borderRadius: "50%",
        background: "rgba(255,255,255,0.10)",
        border: `${Math.max(1, JOY * 0.05)}px solid rgba(255,255,255,0.40)`,
        backdropFilter: "blur(6px)",
        boxShadow: "0 2px 16px rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "grab",
        touchAction: "none",
        userSelect: "none",
        WebkitUserSelect: "none",
        zIndex: 30,
      }}
    >
      {/* Inner knob */}
      <div
        style={{
          width: KNOB,
          height: KNOB,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.30)",
          border: `${Math.max(1, KNOB * 0.08)}px solid rgba(255,255,255,0.60)`,
          pointerEvents: "none",
        }}
      />
      {/* Label */}
      <span
        style={{
          position: "absolute",
          bottom: -BASE_SIZE * 0.45,
          fontSize: BASE_SIZE * 0.22,
          color: "rgba(255,255,255,0.6)",
          whiteSpace: "nowrap",
          pointerEvents: "none",
        }}
      >
        Drag to look
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────
//  D-Pad (left side)
// ─────────────────────────────────────────────
function DPad({ onMoveChange, onJump }: { onMoveChange: (m: MoveDir) => void; onJump: () => void }) {
  const state = useRef<MoveDir>({ forward: false, backward: false, left: false, right: false });

  const set = (key: keyof MoveDir, val: boolean) => {
    state.current = { ...state.current, [key]: val };
    onMoveChange({ ...state.current });
  };

  const padSize = BTN * 3 + BASE_SIZE * 0.16;

  return (
    <div
      style={{
        position: "fixed",
        bottom: BOT,
        left: HOR,
        width: padSize,
        height: padSize,
        zIndex: 30,
        touchAction: "none",
      }}
    >
      {/* Up */}
      <DPadButton
        label="▲"
        style={{ left: GAP, top: 0 }}
        onStart={() => set("forward", true)}
        onEnd={() => set("forward", false)}
      />
      {/* Down */}
      <DPadButton
        label="▼"
        style={{ left: GAP, bottom: 0 }}
        onStart={() => set("backward", true)}
        onEnd={() => set("backward", false)}
      />
      {/* Left */}
      <DPadButton
        label="◀"
        style={{ left: 0, top: GAP }}
        onStart={() => set("left", true)}
        onEnd={() => set("left", false)}
      />
      {/* Right */}
      <DPadButton
        label="▶"
        style={{ right: 0, top: GAP }}
        onStart={() => set("right", true)}
        onEnd={() => set("right", false)}
      />
      {/* Center jump button */}
      <DPadButton
        label="⬆︎"
        style={{
          left: GAP,
          top: GAP,
          background: "rgba(255,220,50,0.25)",
          border: `${Math.max(1, BTN * 0.08)}px solid rgba(255,220,50,0.6)`,
          fontSize: BTN * 0.31,
        }}
        onStart={() => onJump()}
        onEnd={() => {}}
      />
      {/* Center dot */}
      <div
        style={{
          position: "absolute",
          left: GAP + BTN / 2 - BASE_SIZE * 0.16,
          top: GAP + BTN / 2 - BASE_SIZE * 0.16,
          width: BASE_SIZE * 0.32,
          height: BASE_SIZE * 0.32,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.2)",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────
//  GameControls — exported default
// ─────────────────────────────────────────────
export default function GameControls({ onMoveChange, onLookDelta, onJump }: GameControlsProps) {
  return (
    <>
      <DPad onMoveChange={onMoveChange} onJump={onJump} />
      <LookJoystick onLookDelta={onLookDelta} />
    </>
  );
}
